import React, { useState } from "react";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MdAdd, MdCheck, MdClose, MdKeyboardArrowDown } from "react-icons/md";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const Dot = ({ color }) => (
  <span
    aria-hidden="true"
    className="h-[7px] w-[7px] shrink-0 rounded-full"
    style={{ backgroundColor: color || "#9ca3af" }}
  />
);

// The order's route: taken from the loaded routes (current name and colour),
// else from the order itself (a route the list doesn't have, or no list).
export const currentRoute = (row, routes = []) => {
  const id = row?.route_id ?? row?.route?.id;
  if (id == null) return null;
  return (
    routes.find((route) => sameId(route.id, id)) ??
    (sameId(row?.route?.id, id) ? row.route : null)
  );
};

// Options for a Route filter (route_name, which the server matches by
// partial name): one per route name, with its colour and how many orders are
// on it (the routes list's orders_count; routes sharing a name add up, as
// the filter finds them all). Names are the account's own text, so values
// are URL-encoded (BoltTable puts filter values in the query string as they
// are); the chip still shows the name. count is null when it isn't known.
export const routeFilterOptions = (routes = []) => {
  const byName = new Map();
  routes.forEach((route) => {
    const name = String(route?.name ?? "").trim();
    if (!name) return;
    const known = Number.isFinite(Number(route?.orders_count))
      ? Number(route.orders_count)
      : null;
    const option = byName.get(name);
    if (!option) byName.set(name, { color: route?.color, count: known });
    else if (known != null) option.count = (option.count ?? 0) + known;
  });
  return [...byName].map(([name, { color, count }]) => ({
    label: name,
    value: encodeURIComponent(name),
    color,
    count,
  }));
};

// An order moved from one route to another (either may be none): each
// route's orders_count follows, where the list has one.
export const moveRouteCount = (routes, fromId, toId) =>
  String(fromId ?? "") === String(toId ?? "")
    ? routes
    : routes.map((route) => {
        if (!Number.isFinite(Number(route?.orders_count))) return route;
        const step =
          (String(route.id) === String(toId ?? "") ? 1 : 0) -
          (String(route.id) === String(fromId ?? "") ? 1 : 0);
        return step
          ? {
              ...route,
              orders_count: Math.max(0, Number(route.orders_count) + step),
            }
          : route;
      });

/**
 * A self-delivery order's route: the route (colour dot + name) opens a menu
 * to move the order to another route or take it off; without a route,
 * "Assign route" opens the same menu. `routes` are loaded once by the page;
 * `onChange(row, routeId | null)` saves (the button is disabled meanwhile).
 * With no routes to pick from, or `disabled` (e.g. a cancelled order), the
 * route shows as plain text, and nothing shows without one.
 */
const RouteDropdown = ({ row, routes = [], onChange, disabled = false }) => {
  const [anchor, setAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = currentRoute(row, routes);
  const canChange = !disabled && routes.length > 0 && Boolean(onChange);

  if (!canChange) {
    if (!current) return null;
    return (
      <span
        title={`Route: ${current.name}`}
        className="flex min-w-0 items-center gap-[5px] font-medium text-ui-text-muted"
      >
        <Dot color={current.color} />
        <span className="min-w-0 truncate">{current.name}</span>
      </span>
    );
  }

  const options =
    current && !routes.some((route) => sameId(route.id, current.id))
      ? [current, ...routes]
      : routes;

  const choose = async (routeId) => {
    setAnchor(null);
    if (sameId(routeId, current?.id) || (routeId == null && !current)) return;
    setSaving(true);
    try {
      await onChange(row, routeId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={saving}
        aria-busy={saving}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={
          current
            ? `Route ${current.name}. Change the route of #${row?.id}`
            : `Assign a route to #${row?.id}`
        }
        className={`flex min-w-0 items-center gap-[4px] border-0 bg-transparent p-0 font-medium disabled:cursor-wait disabled:opacity-50 ${
          current
            ? "text-ui-text-muted hover:text-ui-text"
            : "text-brand hover:underline"
        }`}
      >
        {current ? (
          <>
            <Dot color={current.color} />
            <span className="min-w-0 truncate">{current.name}</span>
            <MdKeyboardArrowDown className="h-[13px] w-[13px] shrink-0" />
          </>
        ) : (
          <>
            <MdAdd className="h-[12px] w-[12px] shrink-0" />
            <span className="min-w-0 truncate">Assign route</span>
          </>
        )}
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ dense: true, "aria-label": "Routes" }}
        PaperProps={{
          sx: {
            minWidth: 180,
            maxHeight: 320,
            "& .MuiMenuItem-root": {
              minHeight: "28px",
              padding: "2px 10px",
              gap: "8px",
              fontSize: 12,
            },
          },
        }}
      >
        {options.map((route) => (
          <MenuItem
            key={route.id}
            selected={sameId(route.id, current?.id)}
            onClick={() => choose(route.id)}
          >
            <Dot color={route.color} />
            <span className="min-w-0 flex-1 truncate">{route.name}</span>
            {sameId(route.id, current?.id) && (
              <MdCheck className="h-[14px] w-[14px] shrink-0 text-brand" />
            )}
          </MenuItem>
        ))}
        {current && <Divider />}
        {current && (
          <MenuItem onClick={() => choose(null)}>
            <MdClose className="h-[13px] w-[13px] shrink-0 text-ui-text-subtle" />
            <span>Remove from route</span>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default RouteDropdown;
